import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { UserPlus, Pencil, Trash2, Loader2, ShieldAlert, Mail, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser,
} from '../api/api';

const ROLES = [
  { valeur: 'administrateur', libelle: 'Administrateur', aide: 'Accès complet, y compris la gestion des comptes et des réglages.' },
  { valeur: 'dsi', libelle: 'Directeur des Systèmes d’Information', aide: 'Accès complet (profil technique) : réglages, apparence et comptes.' },
  { valeur: 'responsable', libelle: 'Responsable', aide: 'Contenu du site, avis et relation client. Ni comptes ni réglages critiques.' },
  { valeur: 'commercial', libelle: 'Commercial', aide: 'Devis, messages, avis et courriel — relation client.' },
  { valeur: 'comptable', libelle: 'Comptable', aide: 'Suivi des devis, tableau de bord et exports (facturation).' },
  { valeur: 'assistant', libelle: 'Assistant(e) administratif(ve)', aide: 'Traitement des devis, messages et courriel au quotidien.' },
];
const LIBELLE_ROLE = Object.fromEntries(ROLES.map((r) => [r.valeur, r.libelle]));
const COULEUR_ROLE = {
  administrateur: 'bg-purple-100 text-purple-800',
  dsi: 'bg-indigo-100 text-indigo-800',
  responsable: 'bg-blue-100 text-blue-800',
  commercial: 'bg-emerald-100 text-emerald-800',
  comptable: 'bg-amber-100 text-amber-800',
  assistant: 'bg-gray-100 text-gray-800',
};

export const AdminUsers = () => {
  const { user, hasPerm } = useAuth();
  const peutGerer = hasPerm('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);        // {mode, id, email, name, role, password}
  const [enregistre, setEnregistre] = useState(false);
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppression, setSuppression] = useState(false);

  const charger = () => {
    setLoading(true);
    adminListUsers()
      .then(setUsers)
      .catch(() => toast.error('Chargement impossible.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (peutGerer) charger();
    else setLoading(false);
  }, [peutGerer]);

  // Sécurité d'affichage : le menu ne propose la page qu'aux comptes autorisés,
  // mais on protège aussi l'accès direct par URL.
  if (user && !peutGerer) {
    return (
      <Card className="p-12 text-center text-gray-600">
        <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-gray-400" />
        Cette section est réservée aux comptes autorisés à gérer les utilisateurs.
      </Card>
    );
  }

  const ouvrirCreer = () =>
    setForm({ mode: 'creer', email: '', name: '', role: 'assistant', password: '' });

  const ouvrirModifier = (u) =>
    setForm({ mode: 'modifier', id: u.id, email: u.email, name: u.name || '', role: u.role, password: '' });

  const majForm = (champ, valeur) => setForm((f) => ({ ...f, [champ]: valeur }));

  const enregistrer = async (e) => {
    e.preventDefault();
    if (form.mode === 'creer' && (!form.email.trim() || form.password.length < 8)) {
      toast.error('Email requis et mot de passe d’au moins 8 caractères.');
      return;
    }
    if (form.mode === 'modifier' && form.password && form.password.length < 8) {
      toast.error('Le nouveau mot de passe doit faire au moins 8 caractères.');
      return;
    }
    setEnregistre(true);
    try {
      if (form.mode === 'creer') {
        await adminCreateUser({
          email: form.email.trim().toLowerCase(),
          name: form.name.trim() || null,
          role: form.role,
          password: form.password,
        });
        toast.success('Compte créé.');
      } else {
        const donnees = { name: form.name.trim() || null, role: form.role };
        if (form.password) donnees.password = form.password;
        await adminUpdateUser(form.id, donnees);
        toast.success('Compte mis à jour.');
      }
      setForm(null);
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setEnregistre(false);
    }
  };

  const supprimer = async () => {
    setSuppression(true);
    try {
      await adminDeleteUser(aSupprimer.id);
      toast.success('Compte supprimé.');
      setASupprimer(null);
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Suppression impossible.');
    } finally {
      setSuppression(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Utilisateurs</h2>
          <p className="text-gray-600">Gérez les comptes d’administration et leurs rôles.</p>
        </div>
        <Button onClick={ouvrirCreer} className="bg-blue-600 hover:bg-blue-700 gap-2">
          <UserPlus className="w-4 h-4" />
          Ajouter un compte
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {!loading && (
        <div className="space-y-3">
          {users.map((u) => {
            const estMoi = u.id === user?.id;
            return (
              <Card key={u.id} className="p-5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-gray-900">{u.name || u.email}</span>
                    <Badge className={COULEUR_ROLE[u.role] || COULEUR_ROLE.moderator}>
                      {LIBELLE_ROLE[u.role] || u.role}
                    </Badge>
                    {estMoi && <span className="text-xs text-gray-400">(vous)</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{u.email}</span>
                    {u.created_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(u.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => ouvrirModifier(u)} className="gap-1">
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </Button>
                  {!estMoi && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setASupprimer(u)}
                      className="gap-1 text-red-600 hover:bg-red-600 hover:text-white border-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Création / modification */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{form?.mode === 'creer' ? 'Nouveau compte' : 'Modifier le compte'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={enregistrer} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => majForm('email', e.target.value)}
                  disabled={form.mode === 'modifier'}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Nom (facultatif)</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => majForm('name', e.target.value)}
                  placeholder="Ex. Jean Dupont"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <select
                  id="role"
                  value={form.role}
                  onChange={(e) => majForm('role', e.target.value)}
                  className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ROLES.map((r) => (
                    <option key={r.valeur} value={r.valeur}>{r.libelle}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">{ROLES.find((r) => r.valeur === form.role)?.aide}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  {form.mode === 'creer' ? 'Mot de passe' : 'Nouveau mot de passe (laisser vide pour ne pas changer)'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => majForm('password', e.target.value)}
                  placeholder="Au moins 8 caractères"
                  autoComplete="new-password"
                  required={form.mode === 'creer'}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Annuler</Button>
                <Button type="submit" disabled={enregistre} className="gap-2">
                  {enregistre && <Loader2 className="w-4 h-4 animate-spin" />}
                  {form.mode === 'creer' ? 'Créer' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation de suppression */}
      <Dialog open={!!aSupprimer} onOpenChange={(o) => !o && setASupprimer(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer ce compte ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Le compte <span className="font-semibold">{aSupprimer?.email}</span> sera définitivement supprimé.
            Cette action est irréversible.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setASupprimer(null)}>Annuler</Button>
            <Button
              onClick={supprimer}
              disabled={suppression}
              className="bg-red-600 hover:bg-red-700 gap-2"
            >
              {suppression && <Loader2 className="w-4 h-4 animate-spin" />}
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
